import { getLogger } from "@expert/logging";

import { getGamificationApiClient } from "@/sales-tools/api/api";

import type { ScheduleResponse } from "./types";

import { emitExpertHubError, EXPERT_HUB_ACTIONS } from "./expertHubErrorMetric";

const logger = getLogger({ module: "getSchedule" });

/**
 * A schedule represents a working shift when it has a start time or at least one block. Single source
 * of truth for "has a shift" — used both here (to decide the `schedule_empty` signal) and in
 * `useSchedule` (mock-fallback + `hasShift`), so the emitted signal and the rendered UI can't drift.
 */
export function scheduleHasShift(schedule: Pick<ScheduleResponse, "workStart" | "blocks">): boolean {
    return schedule.workStart != null || schedule.blocks.length > 0;
}

function isScheduleResponse(data: unknown): data is ScheduleResponse {
    if (typeof data !== "object" || data === null) return false;
    const obj = data as Record<string, unknown>;
    if (!("date" in obj) || !Array.isArray(obj.blocks)) return false;
    // Every block must carry string start/end times — otherwise toBusinessTime(undefined) would
    // silently map to "now" and place the event at the wrong spot instead of failing the guard.
    return obj.blocks.every((block) => {
        if (block == null || typeof block !== "object") return false;
        const b = block as Record<string, unknown>;
        return typeof b.startTime === "string" && typeof b.endTime === "string";
    });
}

/** Fetch the expert's schedule for a business day (YYYY-MM-DD). Returns null on a shape mismatch. */
export const getSchedule = async (date: string): Promise<ScheduleResponse | null> => {
    const resp: unknown = await getGamificationApiClient().get("v1/schedule", { searchParams: { date } }).json();

    // Emit here (once per fetch), not in the useSchedule consumers — see getPayMetrics for why.
    if (!isScheduleResponse(resp)) {
        logger.warn(
            { scheduleDate: date, responseKeys: resp ? Object.keys(resp as object) : null },
            "Off-call hub: schedule API response did not match expected shape",
        );
        emitExpertHubError(EXPERT_HUB_ACTIONS.scheduleFetchFailed);
        return null;
    }

    // An empty schedule most likely signals a real data problem (WFM/upstream returned no shift),
    // not a legitimate day off
    if (!scheduleHasShift(resp)) {
        emitExpertHubError(EXPERT_HUB_ACTIONS.scheduleEmpty);
    }

    return resp;
};
